// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import { Tab, TabGroup } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TabPresentationProps } from "@insite/mobius/Tab";
import { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Document from "@stanion/XDL/Icons/Document";
import DocumentPdf from "@stanion/XDL/Icons/DocumentPdf";
import { color, ellipsis, xScreen } from "@stanion/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
    attributesPosition = "attributesPosition",
    documentsPosition = "documentsPosition",
}

type Props = WidgetProps & HasProduct;

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    tabContainer?: TabPresentationProps;
    printTitle?: TypographyPresentationProps;
    specificationsWrapper?: InjectableCss;
    tabsWrapper?: InjectableCss;
    tabsGroup?: TabGroupPresentationProps;
    // XNG-Changes
    attributeTypeContent?: InjectableCss;
    attributeBrandType?: InjectableCss;
    attributeType?: InjectableCss;
    documentContainer?: InjectableCss;
    documentsWrapper?: InjectableCss;
    typeStyle?: TypographyPresentationProps;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        color: "common.ui8",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            ${ellipsis(2)}
        `,
    },
    attributeTypeLabelText: {
        size: 14,
        lineHeight: "22px",
        css: css`
            width: 50%;
            margin-right: 11px;
            ${xScreen("lte", "xs")} {
                overflow-wrap: anywhere;
            }
        `,
    },
    attributeTypeValuesText: {
        size: 14,
        lineHeight: "22px",
        css: css`
            width: 50%;
            margin-left: 11px;
            ${xScreen("lte", "xs")} {
                overflow-wrap: anywhere;
            }
        `,
    },
    tabContainer: {
        css: css`
            ${getProp("theme.tab.defaultProps.css")};

            @media print {
                display: none;
            }
        `,
    },
    printTitle: {
        size: 18,
        weight: "bold",
        color: "text",
        css: css`
            letter-spacing: 0;
            line-height: 22px;
            margin-bottom: 0;
            @media not print {
                display: none;
            }
        `,
    },
    attributeType: {
        css: css`
            flex: 0 1 calc(50% - 11px);
            padding: 6px 12px;
            &:nth-child(4n + 1),
            &:nth-child(4n + 2) {
                background: #f4f4f4;
            }
            &:nth-child(4n + 1),
            &:nth-child(4n + 3) {
                margin: 0 22px 0 0;
            }
            ${xScreen("lte", "sm")} {
                flex: 1 1 100%;

                &:nth-child(4n + 1),
                &:nth-child(4n + 3) {
                    margin: 0;
                }

                &:nth-child(4n + 2) {
                    background: ${color("common.white")};
                }
                &:nth-child(4n + 3) {
                    background: #f4f4f4;
                }
            }

            @media print {
                break-inside: avoid;
                page-break-inside: avoid;
                column-break-inside: avoid;
            }
        `,
    },
    attributeBrandType: {
        css: css`
            width: calc(50% - 11px);
            padding: 0 12px;
            background: ${color("common.white")};
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    attributeTypeWrapper: {
        css: css`
            padding: 5px 0;
            display: flex;
        `,
    },
    attributeTypeContent: {
        css: css`
            display: flex;
            flex-wrap: wrap;
        `,
    },
    documentsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            column-gap: 25px;
            row-gap: 15px;
        `,
    },
    documentContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 300px;
        `,
    },
    typeStyle: {
        weight: "bold",
        size: 14,
        lineHeight: "22px",
        css: css`
            display: block;
            margin-bottom: 4px;
            height: 30px;
        `,
    },
    documentLink: {
        typographyProps: {
            size: 14,
            lineHeight: "22px",
            css: css`
                display: flex;
                flex-wrap: nowrap;
            `,
        },
        css: css`
            margin-bottom: 19px;
        `,
    },
};

const styles = specificationsStyles;

const ProductDetailsSpecifications: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications },
    fields: { showDocuments, showAttributes, documentsPosition, attributesPosition },
}) => {
    const renderingDocuments = showDocuments && documents && documents.length > 0;
    const renderingAttributes = showAttributes && (brand || (attributeTypes && attributeTypes?.length > 0));
    const renderingSpecifications = specifications && specifications.length > 0;
    const documentsFirst = documentsPosition === "first";
    const attributesFirst = attributesPosition === "first";

    if (!renderingDocuments && !renderingAttributes && !renderingSpecifications) {
        return null;
    }

    const sortedAttributeTypes = attributeTypes
        ?.slice()
        .sort((a, b) => (a.attributeValues?.[0]?.sortOrder || 0) - (b.attributeValues?.[0]?.sortOrder || 0));

    // XNG-Changes grouped Documents required on FRD
    const groupedDocuments = documents!.reduce((groups: any, document) => {
        const { documentType } = document;
        const groupKey = documentType || "__EMPTY__";
        if (!groups[groupKey]) {
            groups[groupKey] = [];
        }
        groups[groupKey].push(document);
        return groups;
    }, {});

    const documentsTab = renderingDocuments && (
        <Tab
            headline={translate("Documents")}
            data-test-selector="productDetails_specifications_documents"
            tabKey="documents-tab"
            key="documents"
            {...styles.tabContainer}
        >
            <StyledWrapper {...styles.documentsWrapper}>
                {Object.entries(groupedDocuments).map(([groupKey, documents]) => (
                    <StyledWrapper key={groupKey}>
                        <Typography {...styles.typeStyle}>{groupKey !== "__EMPTY__" ? groupKey : "Other"}</Typography>
                        <StyledWrapper {...styles.documentContainer}>
                            {(documents as Array<any>).map(document => (
                                <Link
                                    key={document.id.toString()}
                                    href={document.filePath}
                                    target="_new"
                                    {...styles.documentLink}
                                    data-test-selector={`productDetails_${document.id}`}
                                >
                                    <IconMemo
                                        src={
                                            document.documentType.toLowerCase().startsWith("pdf")
                                                ? DocumentPdf
                                                : Document
                                        }
                                        {...styles.documentIcon}
                                    />
                                    <Typography {...styles.documentNameText}>{document.name}</Typography>
                                </Link>
                            ))}
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </Tab>
    );

    const attributesTab = renderingAttributes && (
        <Tab
            headline={translate("Attributes")}
            data-test-selector="productDetails_specifications_attributes"
            tabKey="attributes-tab"
            key="attributes"
            {...styles.tabContainer}
        >
            {brand && (
                <StyledWrapper {...styles.attributeBrandType}>
                    <StyledWrapper {...styles.attributeTypeWrapper}>
                        <Typography {...styles.attributeTypeLabelText}>{translate("Brand")}</Typography>
                        <Typography {...styles.attributeTypeValuesText}>{brand.name}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.attributeTypeContent}>
                {sortedAttributeTypes &&
                    sortedAttributeTypes.map(attributeType => (
                        <StyledWrapper key={attributeType.id.toString()} {...styles.attributeType}>
                            <StyledWrapper {...styles.attributeTypeWrapper}>
                                <Typography {...styles.attributeTypeLabelText}>
                                    {attributeType.label || attributeType.name}
                                </Typography>
                                <Typography {...styles.attributeTypeValuesText}>
                                    {attributeType
                                        .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                        .join(", ")}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
            </StyledWrapper>
        </Tab>
    );

    const specificationTabs =
        renderingSpecifications &&
        specifications?.map(specification => {
            return (
                <Tab
                    key={specification.id.toString()}
                    tabKey={specification.nameDisplay}
                    headline={specification.nameDisplay}
                    data-test-selector="productDetails_specifications_specification"
                    {...styles.tabContainer}
                >
                    {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                </Tab>
            );
        });

    const tabs = [];

    if (attributesTab && attributesFirst) {
        tabs.push(attributesTab);
    }
    if (documentsTab && documentsFirst) {
        tabs.push(documentsTab);
    }
    if (specificationTabs) {
        tabs.push(...specificationTabs);
    }
    if (attributesTab && !attributesFirst) {
        tabs.push(attributesTab);
    }
    if (documentsTab && !documentsFirst) {
        tabs.push(documentsTab);
    }

    return (
        <StyledWrapper {...styles.tabsWrapper}>
            <TabGroup data-test-selector="productDetails_specifications" {...styles.tabsGroup}>
                {tabs}
            </TabGroup>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsSpecifications),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.documentsPosition,
                displayName: "Documents Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                // horizontal: true,
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showDocuments,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.attributesPosition,
                displayName: "Attributes Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                // horizontal: true,
                fieldType: "General",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showAttributes,
            },
        ],
    },
};

export default widgetModule;
